/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * School Selector - Typeahead search for selecting a school
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { School, Search, X, MapPin, Check, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { useDebounce } from '../../hooks/useDebounce'
import { formatGradeRange, getSchoolTypeInfo } from '../../utils/schoolUtils'
import { createLogger } from '../../utils/logger'

const logger = createLogger('SchoolSelector')

const SchoolSelector = ({ value, onChange, disabled = false, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedSchool, setSelectedSchool] = useState(null)
  const [loadingSchool, setLoadingSchool] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  const debouncedSearch = useDebounce(search, 300)
  const containerRef = useRef(null)
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)

  // Update dropdown position when open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const updatePosition = () => {
        const rect = inputRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        })
      }
      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [isOpen])

  // Load school details when value prop changes or on mount
  useEffect(() => {
    // Always load if we have a value but no selectedSchool data
    // This handles the case where component remounts with same value
    if (value && (!selectedSchool || selectedSchool.id !== value)) {
      loadSchoolDetails(value)
    } else if (!value && selectedSchool) {
      setSelectedSchool(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Force reload school details when component mounts with a value
  // This ensures data loads correctly after PWA navigation
  useEffect(() => {
    if (value) {
      loadSchoolDetails(value)
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load school details by ID
  const loadSchoolDetails = async (schoolId) => {
    try {
      setLoadingSchool(true)
      const response = await api.get(`/schools/${schoolId}`)
      if (response.data.success) {
        setSelectedSchool(response.data.school)
      }
    } catch (error) {
      logger.error('Failed to load school details:', error)
    } finally {
      setLoadingSchool(false)
    }
  }

  // Search for schools
  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      searchSchools(debouncedSearch)
    } else {
      setResults([])
    }
  }, [debouncedSearch])

  const searchSchools = async (query) => {
    try {
      setLoading(true)
      const response = await api.get(`/schools/search?q=${encodeURIComponent(query)}&limit=10`)
      if (response.data.success) {
        setResults(response.data.schools || [])
      }
    } catch (error) {
      logger.error('Failed to search schools:', error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  // Handle school selection
  const handleSelect = (school) => {
    setSelectedSchool(school)
    onChange(school.id)
    setSearch('')
    setResults([])
    setIsOpen(false)
  }

  // Handle clear selection
  const handleClear = () => {
    setSelectedSchool(null)
    onChange(null)
    setSearch('')
    setResults([])
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get school type badge using shared utility
  const getTypeBadge = (school) => {
    const info = getSchoolTypeInfo(school)
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded ${info.bgClass} ${info.textClass}`}>
        {info.label}
      </span>
    )
  }

  // Render dropdown via portal
  const renderDropdown = () => {
    if (!isOpen || (search.length < 2 && results.length === 0)) return null

    const dropdownContent = (
      <div
        ref={dropdownRef}
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 9999
        }}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-80 overflow-y-auto"
      >
        {loading ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Searching...
          </div>
        ) : results.length === 0 ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            {search.length >= 2 ? 'No schools found' : 'Type at least 2 characters to search'}
          </div>
        ) : (
          <ul className="py-1">
            {results.map((school) => (
              <li key={school.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(school)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-start gap-3 transition-colors"
                >
                  <School className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {school.name}
                      </p>
                      {getTypeBadge(school)}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span>
                        {school.city}, {school.state}
                      </span>
                      {school.districtName && (
                        <span className="hidden sm:inline truncate ml-1">
                          - {school.districtName}
                        </span>
                      )}
                    </div>
                    {school.gradeLowest && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {formatGradeRange(school.gradeLowest, school.gradeHighest, {
                          includePrefix: true
                        })}
                      </p>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    )

    return createPortal(dropdownContent, document.body)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected School Display */}
      {selectedSchool ? (
        <div className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <School className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-gray-900 dark:text-gray-100 break-words">
                  {selectedSchool.name}
                </p>
                {getTypeBadge(selectedSchool)}
              </div>
              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-sm text-gray-500 dark:text-gray-400 mt-1">
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span>
                    {selectedSchool.city}, {selectedSchool.state}
                  </span>
                </div>
                {selectedSchool.gradeLowest && (
                  <span className="text-gray-400 dark:text-gray-500">
                    {' '}
                    {formatGradeRange(selectedSchool.gradeLowest, selectedSchool.gradeHighest, {
                      includePrefix: true
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
          {!disabled && (
            <button
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 ml-2"
              title="Remove school"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : loadingSchool ? (
        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Loading school...</span>
        </div>
      ) : (
        <>
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search for your school..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setIsOpen(true)}
              disabled={disabled}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
            )}
          </div>

          {/* Dropdown rendered via portal */}
          {renderDropdown()}
        </>
      )}
    </div>
  )
}

export default SchoolSelector
